import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Image,
  StyleSheet,
  View,
  Modal,
  TouchableOpacity,
  TouchableWithoutFeedback,
} from 'react-native';
import { useImageManipulator, SaveFormat } from 'expo-image-manipulator';
import Icon from '../../Icon';
import { Color, CommonProperties } from '../../../styles';
import BaseText from '../../BaseText';
import { performExpenseAction } from '../../../services/api/expense.service';
import { useNavigation } from '@react-navigation/native';
import { setReceiptsState } from '../../../store/actions/expense';
import { openAlert } from '../../../store/actions/alert';
import { isNullOrEmpty, removeLostReceipt } from '../../../utils/common.utils';
import { selectExpenseStates } from '../../../store/selectors/expense.selector';
import { useUpdateExpenseFromReceipts } from '../../../utils/useUpdateExpenseFromReceipts';
import { IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '../../../utils/image.utils';
import { scale } from '../../../utils/scale.utils';

const MAX_SIZE_MB = 2.75;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const COMPRESSION = 0.9;
const WIDTH_LIMIT = 1200;

const loadingImage = require('../../../assets/loading-receipt.gif');

const getBase64ImageSize = (base64String) => {
  if (!base64String) {
    return 0;
  }

  // Calculate size in bytes
  const sizeInBytes =
    base64String.length * (3 / 4) -
    (base64String.endsWith('==') ? 2 : base64String.endsWith('=') ? 1 : 0);

  return sizeInBytes;
};

const ReceiptScannerModal = ({ route }) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const [showPopup, setShowPopup] = useState(false);
  const { file, actionType = null, subTab, updateExpenseState = true, isPDF = false } =
    route.params || {};
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];
  const receipts = removeLostReceipt(expenseStates?.receipts);
  const { updateExpenseWithReceiptsData } = useUpdateExpenseFromReceipts();
  const context = useImageManipulator(file);

  const optimizeImage = async () => {
    context.resize({ width: WIDTH_LIMIT });

    const image = await context.renderAsync();
    const result = await image.saveAsync({
      format: SaveFormat.JPEG,
      base64: true,
      compress: COMPRESSION,
    });

    const sizeInBytes = getBase64ImageSize(result.base64);

    return {
      base64: `data:image/jpeg;base64,${result.base64}`,
      sizeInBytes,
    };
  };

  // Hide back arrow button during OCR
  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: () => <></>,
    });
  }, [navigation]);

  useEffect(() => {
    const dispatchError = () => {
      dispatch(
        openAlert({
          message:
            'Failed to upload receipt. Please use a supported file type and ensure it is under 9 MB.',
          onConfirmPressed: () => {
            navigation.goBack();
          },
        })
      );
    };
    const uploadReceipt = async () => {
      let attachmentBase64 = file;
      let sizeInBytes = null;

      if (!isPDF) {
        const optimized = await optimizeImage(file);
        attachmentBase64 = optimized.base64;
        sizeInBytes = optimized.sizeInBytes;

        const isInvalidSize = isNullOrEmpty(sizeInBytes) || sizeInBytes > MAX_SIZE_BYTES;
        if (isInvalidSize) {
          dispatchError();
          return;
        }
      }

      const params = {
        ActionCode: 'addReceiptToUser',
        FileName: 'receipt',
        FileType: isPDF ? PDF_MIME_TYPE : IMAGE_MIME_TYPE,
        Attachment: attachmentBase64,
        AllowTextExtract: true,
      };

      const receiptDataResponse = await performExpenseAction(params);
      if (!receiptDataResponse) {
        dispatchError();
        return;
      }
      const receiptData = JSON.parse(receiptDataResponse);
      const newReceiptsList = [
        ...receipts,
        {
          file,
          ...receiptData,
        },
      ];
      dispatch(setReceiptsState(newReceiptsList, actionType, subTab));
      if (!receiptData.textExtractSuccess) {
        setShowPopup(true);
      } else {
        if (updateExpenseState) {
          updateExpenseWithReceiptsData(newReceiptsList, actionType, subTab);
        }
        navigation.goBack();
      }
    };
    uploadReceipt();
  }, [file]);
  const closePopup = () => {
    setShowPopup(false);
    navigation.goBack();
  };

  return (
    <View style={styles.container}>
      <Image style={styles.image} source={loadingImage} />
      <BaseText style={styles.content}>{'One moment while we scan in your receipts'}</BaseText>
      <Modal transparent={true} visible={showPopup}>
        <TouchableWithoutFeedback onPress={closePopup}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <TouchableOpacity style={styles.closeButton} onPress={closePopup}>
                <Icon name="cancel" size={24} color="black" />
              </TouchableOpacity>
              <BaseText style={styles.modalMessage1}>Failure to read receipt.</BaseText>
              <BaseText style={styles.modalMessage}>
                May require manual entry{'\n'}of expense.{'\n'}
              </BaseText>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.accent,
    ...CommonProperties.centerItems,
  },
  content: {
    ...CommonProperties.mt5,
    ...CommonProperties.h1,
    ...CommonProperties.p3,
    textAlign: 'center',
  },
  image: {
    ...CommonProperties.mt5,
    width: '50%',
    height: '50%',
  },
  modalOverlay: {
    flex: scale(1),
    backgroundColor: `#00000080`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: 'white',
    padding: scale(30),
    borderRadius: scale(10),
    alignItems: 'center',
  },
  modalMessage: {
    fontSize: scale(16),
    textAlign: 'center',
    color: '#333',
    marginVertical: scale(5),
  },
  modalMessage1: {
    marginBottom: scale(10),
    fontSize: scale(16),
    textAlign: 'center',
    color: '#333',
    marginVertical: scale(5),
  },
  closeButton: {
    position: 'absolute',
    top: scale(0),
    right: scale(10),
    padding: scale(5),
  },
  closeText: {
    fontSize: scale(18),
    color: '#333',
  },
});

export default ReceiptScannerModal;
